import React, { FunctionComponent, LegacyRef } from 'react';

import { CdnImagePath } from 'lib/cdn-image-path';
import { downloadGlobalDatingTrendsReportAction } from 'lib/seo/seo';
import { throttle } from 'lib/throttle';
import useMediaQuery, { MediaQueryWidth } from 'lib/use-media-query';

import Button, { ButtonColor } from 'components/button/button';
import CardModal, { CardModalProps } from 'components/card/card-modal';
import Cards from 'components/cards/cards';
import { CtaBox, CtaBoxActions, CtaBoxContent, CtaBoxHeader } from 'components/cta-box';
import DangerHtml from 'components/danger-html/danger-html';
import PageSection, { PageSectionProps } from 'components/page/page-section';
import { P2 } from 'components/typography';

export type PageSectionModalsProps = {
  lang?: string;
  title: string;
  cards?: CardModalProps[];
  color?: PageSectionProps['color'];
  styling?: CardModalProps['styling'];
  align?: 'center' | 'start' | undefined;
  iconName?: CardModalProps['iconName'];
  frontContent?: React.ReactNode;
  innerRef?: LegacyRef<HTMLDivElement>;
  disclaimer?: string;
};

const PageSectionModals: FunctionComponent<PageSectionModalsProps> = ({
  lang,
  title,
  cards = [],
  color,
  styling,
  align,
  frontContent,
  innerRef,
  disclaimer,
}) => {
  const overlay = `${CdnImagePath}/landing/assets/photos/overlay.png`;
  const isMobile = useMediaQuery(MediaQueryWidth['navigation-full']);
  const titleRef = React.useRef<HTMLDivElement>(null);

  const measureTitleHeight = () => {
    if (titleRef.current) {
      document.body.style.setProperty(
        '--info-graphic-title-height',
        `${titleRef.current.clientHeight}px`,
      );
    }
  };

  React.useEffect(() => {
    measureTitleHeight();

    const throttledUpdate = throttle(measureTitleHeight, 300);
    global.window.addEventListener('resize', throttledUpdate);

    return () => {
      global.window.removeEventListener('resize', throttledUpdate);
      document.body.style.removeProperty('--info-graphic-title-height');
    };
  }, []);

  return (
    <PageSection
      padding="top"
      color={color}
      extraClass={`page__section--relative page__section--overflow page__section--overlay page__section--max-width ${lang === 'fr' ? 'page__section--smaller-title' : ''}`}
      inner={false}
      style={{ ['--background-image' as string]: `url(${overlay})` }}
    >
      {frontContent}
      <div className="page__section-inner is-action--more-gap" ref={innerRef}>
        <CtaBox align={align || 'start'}>
          <CtaBoxHeader tag="h2">
            <div ref={titleRef}>
              <DangerHtml html={title} />
            </div>
          </CtaBoxHeader>
          <CtaBoxContent width="stretch">
            <Cards>
              {cards.map((card, index) => (
                <CardModal
                  imageSrc={card.imageSrc}
                  iconName={card.iconName}
                  onClick={card.onClick}
                  href={card.href}
                  title={card.title}
                  text={card.text}
                  alt={card.alt}
                  key={`card-${index}`}
                  titleTag={card.titleTag}
                  backgroundColor={card.backgroundColor}
                  styling={styling}
                />
              ))}
            </Cards>
          </CtaBoxContent>
          <CtaBoxActions>
            <Button
              text={'Download the full report'}
              color={ButtonColor.DEFAULT}
              extraClass="button-primary yellow"
              href={downloadGlobalDatingTrendsReportAction}
              seo={{
                label: 'download-global-dating-trends-2025-report',
              }}
            />
          </CtaBoxActions>
          <CtaBoxContent>
            <div
              style={{
                minWidth: isMobile ? '100%' : 580,
                maxWidth: 'calc(100vw - 64px)',
                padding: 24,
                backgroundColor: '#fff',
                color: '#1D252C',
                fontWeight: 400,
              }}
            >
              <P2 html={disclaimer} />
            </div>
          </CtaBoxContent>
        </CtaBox>
      </div>
    </PageSection>
  );
};

export default PageSectionModals;
